import { alpha } from '@mui/material/styles';
import basicTheme from '../basicTheme.js';
import typography from '../typography.js';

/* eslint-disable @typescript-eslint/ban-ts-comment */
const outline = {
    '&:enabled:before': {
        borderRadius: '7px',
        backgroundColor: 'transparent',
        transition: `border ${basicTheme.transitions.duration.fast} linear`,
        border: `1px solid ${basicTheme.palette.primary.light}`,
        content: "''",
        display: 'none',
        position: 'absolute',
        top: '-3px',
        left: '-3px',
        right: '-3px',
        bottom: '-3px',
    },
    '&:focus-visible:before': {
        display: 'block',
    },
};
const getLoaderStyle = ({ size, color }) => ({
    '.MuiCircularProgress-root': {
        height: `${size} !important`,
        width: `${size} !important`,
        color,
    },
});
const commonDisabledStyle = Object.assign({ color: basicTheme.palette.N30.main, cursor: 'not-allowed', pointerEvents: 'all' }, getLoaderStyle({ color: basicTheme.palette.N30.main }));
const buttonStyleOverrides = {
    defaultProps: {
        disableRipple: true,
        variant: 'primary',
    },
    styleOverrides: {
        root: Object.assign(Object.assign({ borderRadius: '4px', transition: `color ${basicTheme.transitions.duration.fast} linear, background-color ${basicTheme.transitions.duration.fast} linear, border ${basicTheme.transitions.duration.fast} linear, fill ${basicTheme.transitions.duration.fast} linear`, borderWidth: '1px', color: basicTheme.palette.common.white, backgroundColor: 'transparent', lineHeight: 1 }, getLoaderStyle({ size: '18px', color: basicTheme.palette.common.white })), { '&:disabled': {
                pointerEvents: 'all',
                cursor: 'not-allowed',
            }, ' > svg': {
                fill: 'currentcolor',
            } }),
        // @ts-ignore
        sizeSmall: Object.assign(Object.assign({ height: '20px', minWidth: '65px' }, typography.button2), getLoaderStyle({ size: '14px' })),
        iconSizeSmall: {
            ' > svg': {
                fill: 'currentcolor',
            },
        },
        sizeMedium: Object.assign({ height: '30px', minWidth: '82px' }, typography.button),
        iconSizeMedium: {
            ' > svg': {
                fill: 'currentcolor',
            },
        },
        sizeLarge: Object.assign({ height: '36px', minWidth: '94px' }, typography.button),
        iconSizeLarge: {
            ' > svg': {
                fill: 'currentcolor',
            },
        },
    },
    variants: [
        {
            props: { variant: 'primary' },
            style: Object.assign(Object.assign({ backgroundColor: basicTheme.palette.primary.main, boxShadow: 'none', '&:hover': {
                    backgroundColor: basicTheme.palette.primary.light,
                    color: basicTheme.palette.common.white,
                } }, outline), { '&:active:enabled': {
                    backgroundColor: basicTheme.palette.primary.dark,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: basicTheme.palette.N50.main }) }),
        },
        {
            props: { variant: 'unactive' },
            style: Object.assign(Object.assign({ backgroundColor: basicTheme.palette.N40.main, color: basicTheme.palette.N10.main, '&:hover': {
                    backgroundColor: basicTheme.palette.N30.main,
                    color: basicTheme.palette.white.main,
                } }, outline), { '&:active:enabled': {
                    backgroundColor: basicTheme.palette.N50.main,
                    color: basicTheme.palette.N05.main,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: basicTheme.palette.N50.main, color: basicTheme.palette.N30.main }) }),
        },
        {
            props: { variant: 'muted' },
            style: Object.assign(Object.assign({ backgroundColor: basicTheme.palette.N40.main, color: basicTheme.palette.N10.main, '&:hover': {
                    backgroundColor: basicTheme.palette.N30.main,
                    color: basicTheme.palette.white.main,
                } }, outline), { '&:active:enabled': {
                    backgroundColor: basicTheme.palette.N50.main,
                    color: basicTheme.palette.N05.main,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: basicTheme.palette.N50.main, color: basicTheme.palette.N30.main }) }),
        },
        {
            props: { variant: 'secondary' },
            style: Object.assign(Object.assign({ backgroundColor: basicTheme.palette.N30.main, '&:hover': {
                    backgroundColor: basicTheme.palette.N20.main,
                    color: basicTheme.palette.common.white,
                } }, outline), { '&:active:enabled': {
                    backgroundColor: basicTheme.palette.N40.main,
                    color: basicTheme.palette.common.white,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: basicTheme.palette.N70.main }) }),
        },
        {
            props: { variant: 'ghost' },
            style: Object.assign(Object.assign({ border: '1px solid', borderColor: basicTheme.palette.primary.main, color: basicTheme.palette.primary.main }, getLoaderStyle({ color: basicTheme.palette.primary.main })), { '&:hover': {
                    borderColor: basicTheme.palette.primary.light,
                    color: basicTheme.palette.primary.light,
                    backgroundColor: 'transparent',
                }, '&:focus-visible:enabled': {
                    backgroundColor: alpha(basicTheme.palette.primary.main, 0.2),
                    border: `1px solid ${basicTheme.palette.primary.main}`,
                    color: basicTheme.palette.primary.main,
                }, '&:active:enabled': {
                    backgroundColor: alpha(basicTheme.palette.primary.main, 0.2),
                    border: `1px solid ${basicTheme.palette.primary.main}`,
                    color: basicTheme.palette.primary.main,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { borderColor: basicTheme.palette.N30.main }) }),
        },
        {
            props: { variant: 'text' },
            style: Object.assign(Object.assign(Object.assign(Object.assign({ color: basicTheme.palette.primary.main }, getLoaderStyle({ color: basicTheme.palette.primary.main })), { '&:hover': {
                    color: basicTheme.palette.primary.light,
                    backgroundColor: alpha(basicTheme.palette.primary.main, 0.2),
                } }), outline), { '&:active:enabled': {
                    backgroundColor: alpha(basicTheme.palette.primary.main, 0.2),
                    color: basicTheme.palette.primary.main,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: 'transparent' }) }),
        },
        {
            props: { variant: 'danger' },
            style: Object.assign({ backgroundColor: basicTheme.palette.error.main, boxShadow: 'none', '&:hover': {
                    backgroundColor: basicTheme.palette.error.light,
                    color: basicTheme.palette.common.white,
                }, '&:active:enabled': {
                    backgroundColor: basicTheme.palette.error.dark,
                }, '&.Mui-disabled': Object.assign(Object.assign({}, commonDisabledStyle), { backgroundColor: basicTheme.palette.N50.main }) }, outline),
        },
    ],
};

export { buttonStyleOverrides as default };
//# sourceMappingURL=MuiButton.js.map
